import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import CheckBox from 'components/final-form/CheckBox';
import CheckBoxThreeState from 'components/final-form/CheckBoxThreeState';
import { dateInput } from 'components/final-form/DateInput';
import {
  DropdownOption,
  mapToDropdownOptionArray,
  stringArrayToDropdownOptionArray
} from 'components/final-form/dropdownUtils';
import Input from 'components/final-form/Input';
import RadioGroup from 'components/final-form/RadioGroup';
import { radioGroupInput } from 'components/final-form/RadioGroupInput';
import { generateRadioButtonOptions, generateRadioButtonOptionsFromEnum } from 'components/final-form/radioUtils';
import Select from 'components/final-form/Select';
import { selectInput } from 'components/final-form/SelectInput';
import { textInput } from 'components/final-form/TextInput';
import { enabledTestFrequencies } from 'components/guarantors/employee/employeeUtils';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import { IndexedZipData } from 'components/patient/upsert/UpsertPatient';
import { sortZipData } from 'components/patient/utils/patientUtils';
import { FormApi } from 'final-form';
import createDecorator from 'final-form-calculate';
import React, { Component } from "react";
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Header, RadioProps } from 'semantic-ui-react';
import { getCompanyExternalCodeFromCompanyArticleGroupsWithCertificates } from 'services/companyServices';
import { getAllCountries } from 'services/countryServices';
import { editEmployee, saveEmployee } from 'services/employeeServices';
import { searchGuarantors } from 'services/guarantorServices';
import { searchZipDataForInput } from 'services/zipDataServices';
import { CovidTestSampleMethod, RegistrationStateType, TestRegistrationType, ZipDataOrderBy } from 'ts-types/api.enums';
import {
  CompanyDto,
  DivisionDto,
  GuarantorDto,
  GuarantorSearchRequest,
  MedicamentDto,
  UpsertEmployeeDto,
  UpsertPatientDto
} from 'ts-types/api.types';
import { errorUtils } from 'utils/errorUtils';
import { noop } from 'utils/functionUtils';
import { IndexSignature } from 'utils/praxTypes';
import { ahvValidator, required } from 'utils/validatorUtils';

interface Props extends AuthConsumerRenderProps, MessageSourceProps {
  toggleAddEditModal: () => void,
  onSave: () => void,
  onCancel: () => void,
  companyId: number,
  company: CompanyDto,
  employeeId?: number,
  employee?: UpsertEmployeeDto,
  guarantor?: GuarantorDto,
  companyDivisions: Array<DivisionDto>,
  companyShippable: Array<MedicamentDto>,
}

interface State {
  errorMessage?: string,
  genders: RadioProps[],
  testRegistrationTypes: RadioProps[],
  testFrequencies: RadioProps[],
  countries?: DropdownOption[],
  cities: DropdownOption[],
  zip: DropdownOption[],
  languages: DropdownOption[],
  divisions: DropdownOption[],
  employeeGuarantors: DropdownOption[],
  medicaments: DropdownOption[],
  testTypes: DropdownOption[],
  testSampleMethods: DropdownOption[],
  cancelTokenSource: CancelTokenSource
}

class UpsertEmployeeForm extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const {employee, companyDivisions, companyShippable, getMessage, guarantor} = this.props;
    const cancelTokenSource = axios.CancelToken.source();

    const languageLabels: string[] = ["german", "french", "italian", "english"];
    const languageKeys: string[] = ["de", "fr", "it", "en"];
    const genderLabels: string[] = ["male", "female", "other"];
    const testRegTypesLabels: string[] = Object.values(TestRegistrationType);
    const testSampleMethodsLabels: string[] = Object.values(CovidTestSampleMethod);
    const testFrequenciesLabels: string[] = enabledTestFrequencies;

    getAllCountries(cancelTokenSource)
        .then(response => {
          this.setState({
            countries: mapToDropdownOptionArray(response, "name", "shortName")
          })
        }).catch(noop);


    const testFrequencies = generateRadioButtonOptionsFromEnum(
        testFrequenciesLabels,
        "testFrequency",
        getMessage
    );

    const testRegistrationTypes = generateRadioButtonOptionsFromEnum(
        testRegTypesLabels,
        "testRegistrationType",
        getMessage
    );
    const initialCities: Array<DropdownOption> = [];
    const initialZip: Array<DropdownOption> = [];
    const initialGuarantor: Array<DropdownOption> = [];

    if (employee) {

      if (employee.city) {
        initialCities.push({
          key: employee.city,
          text: employee.city,
          value: employee.city,
          predefinedOption: true
        })
      }

      if (employee.zip) {
        initialZip.push({
          key: employee.zip,
          text: employee.zip,
          value: employee.zip,
          predefinedOption: true
        })
      }

      if (employee.guarantorId && guarantor) {
        initialGuarantor.push({
          key: employee.guarantorId,
          text: `${guarantor.description}, ${guarantor.city}`,
          value: employee.guarantorId,
          predefinedOption: true
        })
      }
    }

    this.state = {
      genders: generateRadioButtonOptions(genderLabels, "gender", getMessage),
      testRegistrationTypes: testRegistrationTypes,
      testFrequencies: testFrequencies,
      cities: initialCities,
      zip: initialZip,
      languages: stringArrayToDropdownOptionArray(languageLabels, getMessage, "language", languageKeys),
      divisions: mapToDropdownOptionArray(companyDivisions, "name", "id", "id"),
      employeeGuarantors: initialGuarantor,
      medicaments: mapToDropdownOptionArray(companyShippable, "description", "id", "id"),
      testTypes: [],
      testSampleMethods: stringArrayToDropdownOptionArray(
          testSampleMethodsLabels, getMessage, "covidTestSampleMethod"),
      cancelTokenSource
    }

    getCompanyExternalCodeFromCompanyArticleGroupsWithCertificates(props.companyId, cancelTokenSource)
        .then(response => {
          this.setState({
            testTypes: stringArrayToDropdownOptionArray(
                response, getMessage, "enum.InterventionExternalCode")
          })
        })
  };

  componentWillUnmount(): void {

    const {cancelTokenSource} = this.state;
    cancelTokenSource.cancel('Operation canceled by user');
  }

  initialEmployeeValues = (): Partial<UpsertEmployeeDto> => {

    const {currentUser, companyId, company} = this.props;
    const {countries} = this.state;

    return {
      mandateId: currentUser ? currentUser.userConfig.mandateId : 1,
      companyId: companyId,
      active: true,
      companyAdmin: false,
      testFrequency: "WEEK_ONE",
      testRegistrationType: "CENTRAL",
      name: "",
      firstName: "",
      street: "",
      zip: "",
      city: "",
      birthDate: "",
      telephone: "",
      ahvNumber: "",
      language: "de",
      countryId: countries ? countries[0].value : undefined,
      sex: 2,
      automaticCertificateNotification: company.automaticCertificateNotification,
      acceptCD: company.acceptCD,
      acceptCDRemark: company.acceptCDRemark,
    };
  };

  mapZipData = (field: string, zipData: Array<IndexedZipData>, orderBy: ZipDataOrderBy): Array<DropdownOption> => {

    const sortedZipData = sortZipData(zipData, orderBy);

    return sortedZipData.map(data => ({
      key: data.id,
      text: data[field],
      value: `${data.zip}, ${data.city}, ${data.countryId}`,
      content: `${data.zip}, ${data.city}, ${data.canton}`,
      predefinedOption: true
    }))
  };

  searchZipData = (query: string, countryId: number) => {

    const {cities, zip: zips, cancelTokenSource} = this.state;

    searchZipDataForInput(query, countryId, cancelTokenSource, 75)
        .then(response => {

          const allInitialAndNewCityAdditions = cities.filter(city => city.value == city.key);
          const allInitialAndNewZipAdditions = zips.filter(zip => zip.value == zip.key);

          this.setState({
            cities: [...allInitialAndNewCityAdditions, ...this.mapZipData("city", response, ZipDataOrderBy.CITY)],
            zip: [...allInitialAndNewZipAdditions, ...this.mapZipData("zip", response, ZipDataOrderBy.ZIP)]
          })
        })
        .catch(() => {
          this.setState({
            cities: [],
            zip: []
          })
        })
  };

  onNewAddition = (options: "zip" | "cities") => (newAddition: DropdownOption) => {

    const {cities, zip: zips} = this.state;

    if (options == "cities") {

      const foundCity = cities.find(city => city.key == newAddition.key);

      if (!foundCity) {
        newAddition.predefinedOption = false;
        const newCities = cities.filter(city => city.predefinedOption === true);
        this.setState({
          cities: [newAddition, ...newCities]
        });
      }
    } else if (options == "zip") {

      const foundZip = zips.find(zip => zip.key == newAddition.key);

      if (!foundZip) {
        newAddition.predefinedOption = false;
        const newZips = zips.filter(city => city.predefinedOption === true);
        this.setState({
          zip: [newAddition, ...newZips]
        });
      }
    }
  };

  updateZipDataFieldDecorator = (field: string) =>
      (zipData?: string, allValues?: Partial<UpsertPatientDto & IndexSignature>) => {

        if (zipData) {
          const splitZipData = zipData.split(", ");

          if (splitZipData.length == 3 && allValues && allValues[field] != zipData) {
            return zipData;
          }
        }

        return allValues ? allValues[field] : undefined;
      };

  zipDataDecorator = createDecorator(
      {
        field: "city",
        updates: {
          zip: this.updateZipDataFieldDecorator("zip")
        }
      },
      {
        field: "zip",
        updates: {
          city: this.updateZipDataFieldDecorator("city")
        }
      }
  );

  searchGuarantor = (description: string) => {

    const {cancelTokenSource} = this.state;
    const tariffTypeKVG = "10";

    const request: GuarantorSearchRequest = {
      description: description,
      city: '',
      includesInactive: true,
      empty: false,
      bagnumber: "",
      tariffType: tariffTypeKVG
    }

    if (description !== "") {
      searchGuarantors(request, cancelTokenSource)
          .then(response => {
            this.setState({
              employeeGuarantors: this.mapGuarantorsData(response)
            })
          })
          .catch(() => {
            this.setState({
              employeeGuarantors: []
            })
          });
    } else {
      this.setState({
        employeeGuarantors: []
      })
    }
  }

  mapGuarantorsData = (guarantorData: Array<GuarantorDto>): Array<DropdownOption> => {

    return guarantorData.map(data => ({
      key: data.id,
      text: `${data.description}, ${data.city} `,
      value: data.id,
      content: `${data.description}, ${data.city}`,
      predefinedOption: true
    }))
  };

  resetAdminDivisionDecorator = createDecorator({
    field: "companyAdmin",
    updates: {
      adminDivisionId: (fieldValue, allValues?: Partial<UpsertEmployeeDto>) => {
        return !fieldValue || !allValues ? undefined : allValues.adminDivisionId;
      }
    }
  });

  resetAcceptCDRemark = createDecorator({
    field: 'acceptCD',
    updates: {
      acceptCDRemark: (fieldValue, allValues?: Partial<UpsertEmployeeDto>) => {
        return fieldValue && allValues ? allValues.acceptCDRemark : undefined;
      }
    }
  });

  handleError(error: any) {
    const {getMessage} = this.props;
    const errorCode = error.errorCode;
    const knownErrors: Array<string> = [
      errorUtils.invalidInput,
      errorUtils.invalidEmail,
      errorUtils.duplicateEmail,
      errorUtils.employeeEmailAlreadyExists
    ];

    const violations: Array<any> = error.violations;

    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          this.setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    } else {
      if (knownErrors.includes(errorCode)) {
        this.setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        this.setErrorMessage(getMessage('error.general'));
      }
    }
  };

  handleSubmit = async (values: Partial<UpsertEmployeeDto>) => {
    const {employeeId, onSave, company} = this.props;
    const {cancelTokenSource} = this.state;

    let zip = "";
    let city = "";

    if (values.zip) {
      if (values.zip.includes(', ')) {
        zip = values.zip.split(", ")[0];
      } else {
        zip = values.zip;
      }
    }

    if (values.city) {
      if (values.city.includes(', ')) {
        city = values.city.split(", ")[1];
      } else {
        city = values.city;
      }
    }

    let admin = values.companyAdmin;
    if (company.guarantorCompany) {
      admin = true;
    }

    const upserEmployeeDto = {
      ...values,
      zip: zip,
      city: city,
      companyAdmin: admin
    };

    if (employeeId) {
      await editEmployee(employeeId, upserEmployeeDto, cancelTokenSource)
          .then(onSave)
          .catch((e: any) => this.handleError(e.response.data))
    } else {
      await saveEmployee(upserEmployeeDto, cancelTokenSource)
          .then(onSave)
          .catch((e: any) => this.handleError(e.response.data))
    }
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  render(): React.ReactNode {

    const {employee = this.initialEmployeeValues()} = this.props;

    return (
        <FinalForm
            onSubmit={(values) => this.handleSubmit(values)}
            decorators={[this.zipDataDecorator, this.resetAdminDivisionDecorator, this.resetAcceptCDRemark]}
            initialValues={employee}
            subscription={{pristine: true, submitting: true, values:true}}
            render={this.renderForm}
        />
    );
  }

  renderForm = ({form, handleSubmit, submitting, values}: FormRenderProps): React.ReactNode => {

    const {getMessage, company, onCancel} = this.props;
    const {errorMessage} = this.state;

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              {errorMessage
              && (
                  <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                    {errorMessage}
                  </StyledErrorMessage>
              )
              }
              <PraxFormGrid>
                {this.renderBasicInformationSection(form)}
                {!company.guarantorCompany && this.renderTestTypesSection(values)}
              </PraxFormGrid>
            </FormContent>

            <FormButtonsRow>
              <Button compact type="submit" primary disabled={submitting}>
                {getMessage("button.save")}
              </Button>
              <Button compact type="button" disabled={submitting}
                      onClick={onCancel}>{getMessage("button.cancel")}
              </Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    );
  };

  ahvValidator = (value: string) => ahvValidator(value);

  renderBasicInformationSection = (form: FormApi): React.ReactNode => {

    const {countries, cities, zip, divisions, genders, medicaments, languages, employeeGuarantors} = this.state;
    const {getMessage, company} = this.props;

    const validator = (value: string) => required(value);

    const selectMap: IndexSignature = {
      medicamentId: medicaments,
      divisionId: divisions,
      adminDivisionId: divisions,
      countryId: countries,
      city: cities,
      zip: zip,
      guarantorId: employeeGuarantors,
      language: languages
    };

    const countryId = form.getState().values.countryId;

    const basicLeftInfoFields = [
      textInput({
        name: "name",
        validate: validator
      }),
      textInput({
        name: "firstName",
        validate: validator
      }),
      dateInput({
        name: "birthDate",
        validate: !company.guarantorCompany ? validator : undefined
      }),
      textInput({
        name: "street",
      }),
      selectInput({
            name: "zip",
            allowAdditions: true,
            remoteSearch: true,
            customRemoteSearchMethod: ((searchQuery: string) => this.searchZipData(searchQuery, countryId)),
            onNewAddition: (this.onNewAddition("zip"))
          },
          selectMap
      ),
      selectInput({
            name: "city",
            allowAdditions: true,
            remoteSearch: true,
            customRemoteSearchMethod: ((searchQuery: string) => this.searchZipData(searchQuery, countryId)),
            onNewAddition: (this.onNewAddition("cities"))
          },
          selectMap
      ),
      selectInput({
            name: "countryId",
            validate: validator
          },
          selectMap
      )
    ];
    const basicRightInfoFields = [
      radioGroupInput({name: "sex"}, genders, "33%"),
      selectInput({
            name: "divisionId",
            clearable: true
          },
          selectMap
      ),
      textInput({
        name: "telephone"
      }),
      textInput({
        name: "companyEmail",
      }),
      textInput({
        name: "ahvNumber",
        validate: this.ahvValidator
      }),
      selectInput({
          name: "guarantorId",
          customRemoteSearchMethod: (searchQuery: string) => this.searchGuarantor(searchQuery),
          clearable: true,
          remoteSearch: true,
          fluid: true
        },
        selectMap
      ),
      textInput({
        name: "passportNumber",
      }),
      selectInput({
            name: "language",
            validate: validator
          },
          selectMap
      ),
      textInput({
            name: "remark",
          }
      )
    ];

    const mappedLeftRow = basicLeftInfoFields.map((fieldProps, index) => {

      return (
          <Grid.Row key={index}>
            <Grid.Column width={5} verticalAlign="middle">
              {`${getMessage(`employee.${fieldProps.name}`)}:`}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  fluid
                  {...fieldProps}
              />
            </Grid.Column>
          </Grid.Row>
      );
    });

    const mappedRightRow = basicRightInfoFields.map((fieldProps, index) => {

      return (
          <Grid.Row key={index}>
            <Grid.Column width={5} verticalAlign="middle">
              {`${getMessage(`employee.${fieldProps.name}`)}:`}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  fluid
                  {...fieldProps}
              />
            </Grid.Column>
          </Grid.Row>
      );
    });

    return (
        <React.Fragment>
          <Grid.Row>
            <Grid.Column width={8} verticalAlign="middle" textAlign="left">
              <Header as='h5'>{getMessage("employee.basicInformation")}</Header>
            </Grid.Column>
            <Grid.Column width={8} textAlign="right">
              <Field
                  name="active"
                  component={CheckBox}
                  label={getMessage("active")}
                  toggle
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={8}>
              <PraxFormGrid>
                {mappedLeftRow}
              </PraxFormGrid>
            </Grid.Column>
            <Grid.Column width={8}>
              <PraxFormGrid>
                {mappedRightRow}
              </PraxFormGrid>
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>
    );
  };


  renderTestTypesSection = (values: any): React.ReactNode => {

    const {
      testRegistrationTypes,
      testFrequencies,
      testTypes,
      testSampleMethods,
      divisions
    } = this.state;

    const {employeeId, employee, getMessage} = this.props;

    const disableAdminDivision = !values.companyAdmin;
    const disableAcceptCD = employeeId && employee
        && (employee.acceptCD || employee.registrationState == RegistrationStateType.ACCEPTED);

    const disableAcceptCDRemark = !values.acceptCD;

    return (
        <React.Fragment>
          <Grid.Row>
            <Grid.Column style={{paddingTop: "0.5rem"}} width={8}>
              <Header as='h5'>{getMessage("employee.testInfo")}</Header>
            </Grid.Column>
            <Grid.Column width={3}>
              &nbsp;
            </Grid.Column>
            <Grid.Column width={5}>
              <Field
                  label={getMessage("employee.companyAdmin")}
                  name="companyAdmin"
                  component={CheckBox}
                  toggle
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3}>
              {`${getMessage("employee.testType")}:`}
            </Grid.Column>
            <Grid.Column width={5}>
              <Field
                  name="testType"
                  component={Select}
                  options={testTypes}
                  fluid
                  validate={required}
              />
            </Grid.Column>
            <Grid.Column width={3}>
              {`${getMessage("employee.adminForDivision")}:`}
            </Grid.Column>
            <Grid.Column width={5}>
              <Field
                  name="adminDivisionId"
                  component={Select}
                  options={divisions}
                  clearable={true}
                  disabled={disableAdminDivision}
                  width="100%"
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3}>
              {`${getMessage("testSampleMethod")}:`}
            </Grid.Column>
            <Grid.Column width={5}>
              <Field
                  name="testSampleMethod"
                  component={Select}
                  options={testSampleMethods}
                  fluid
                  clearable
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3}>
            </Grid.Column>
            <Grid.Column width={13}>
              <Field
                  name="generateCertificate"
                  component={CheckBoxThreeState}
                  label={`${getMessage("company.generateCertificate")}`}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3}>
            </Grid.Column>
            <Grid.Column width={13}>
              <Field
                  name="generateReport"
                  component={CheckBoxThreeState}
                  label={`${getMessage("company.generateReport")}`}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3}>
              {`${getMessage("employee.testRegistrationType")}:`}
            </Grid.Column>
            <Grid.Column width={13}>
              <Field
                  name="testRegistrationType"
                  component={RadioGroup}
                  radioDefinitions={testRegistrationTypes}
                  radiowidth="40%"
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3}>
              {`${getMessage("employee.testFrequency")}:`}
            </Grid.Column>
            <Grid.Column width={13}>
              <Field
                  name="testFrequency"
                  component={RadioGroup}
                  radioDefinitions={testFrequencies}
                  radiowidth="40%"
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3}>
            </Grid.Column>
            <Grid.Column width={5}>
              <Field
                  name="acceptCD"
                  component={CheckBox}
                  label={`${getMessage("employee.acceptContractDocuments")}`}
                  disabled={disableAcceptCD}
              />
            </Grid.Column>
            <Grid.Column width={8} verticalAlign="middle">
              <Field
                  name="acceptCDRemark"
                  component={Input}
                  disabled={disableAcceptCD || disableAcceptCDRemark}
                  fluid
              />
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>
    );
  };

}

export default withAuthContext(withMessages(UpsertEmployeeForm));
